import { useBundle } from '@amzn/react-arb-tools';
import {
  PREFERS_COLOR_SCHEME_ID,
  token,
} from '@amzn/stencil-design-tokens/js/web/utils';
import { MainWithSkipLink } from '@amzn/stencil-react-components/a11y';
import {
  ThemeMode,
  useStencilContext,
} from '@amzn/stencil-react-components/context';
import {
  AppLayout,
  AppLayoutHeader,
  AppLayoutMaxWidth,
  AppLayoutNav,
  Col,
  Row,
} from '@amzn/stencil-react-components/layout';
import {
  Link,
  LinkTextDecorationOptions,
} from '@amzn/stencil-react-components/link';
import { useBreakpoints } from '@amzn/stencil-react-components/responsive';
import {
  SecondaryNav,
  SecondaryNavLink,
  SecondaryNavList,
} from '@amzn/stencil-react-components/secondary-nav';
import { Text } from '@amzn/stencil-react-components/text';
import React, { PropsWithChildren } from 'react';
import { trackClick } from 'src/analytics/clickTracking';
import {
  CAREERS_LINK,
  CONDITIONS_OF_USE_LINK,
  NAV_MESSAGE_BUNDLE_NAME,
  PRIVACY_NOTICE_LINK,
} from 'src/constants';
import { NAV_LAYOUT_PAGE_GROUP } from 'src/constants/MonitoringConstants';
import { navigateToSection } from 'src/utils/redirectUtils';
import { t } from '../helpers';
import amazonLogoWhiteText from '../images/amazon-white-text.png';
import amazonLogo from '../images/amazon.png';
import gtcLogoWhite from '../images/gtc-in-this-together-white.svg';
import gtcLogo from '../images/gtc-in-this-together.svg';

const GrowThriveConnectLayout = ({ children }: PropsWithChildren) => {
  const [bundle] = useBundle(NAV_MESSAGE_BUNDLE_NAME);

  const { matches } = useBreakpoints();
  const { themeMode } = useStencilContext();

  const isDarkMode = themeMode === ThemeMode.Dark;

  const hasHeader = matches.xl || matches.l;

  function footerWrapper(disclaimers: React.ReactNode[]): React.ReactNode {
    return matches.m || matches.s ? (
      <Col width={'100%'} alignItems="center" dataTestId="footer-small-test-id">
        {disclaimers.map((text, index) => {
          return (
            <Col
              key={index}
              width={'100%'}
              gridGap={'dimensions.spacing.400'}
              alignItems="center"
              padding={{
                left: 'dimensions.spacing.500',
                right: 'dimensions.spacing.500',
                top: 'dimensions.spacing.200',
                bottom: 'dimensions.spacing.200',
              }}
            >
              {text}
            </Col>
          );
        })}
      </Col>
    ) : (
      <Row width={'100%'} dataTestId="footer-large-test-id">
        <Row
          width={'100%'}
          gridGap={'dimensions.spacing.400'}
          justifyContent="flex-start"
          padding={{
            left: 'dimensions.spacing.500',
            right: 'dimensions.spacing.500',
            top: 'dimensions.spacing.400',
            bottom: 'dimensions.spacing.400',
          }}
        >
          {disclaimers}
        </Row>
      </Row>
    );
  }

  return (
    <AppLayout>
      {hasHeader && (
        <AppLayoutHeader
          dataTestId="app-layout-header-test-id"
          backgroundColor={'color.surface.bg-muted'}
        >
          <Row
            gridGap="S300"
            justifyContent="space-between"
            padding={{
              left: 'dimensions.spacing.500',
              right: 'dimensions.spacing.500',
              top: 'dimensions.spacing.250',
              bottom: 'dimensions.spacing.250',
            }}
          >
            <img
              data-test-id="gtc-logo-test-id"
              src={isDarkMode ? gtcLogoWhite : gtcLogo}
              style={{
                width: 'auto',
                height: '2.625rem',
              }}
              alt={t(bundle, 'gtc_logo_alt')}
              loading="lazy"
              aria-hidden="true"
            />
            <img
              data-test-id="amazon-logo-test-id"
              src={isDarkMode ? amazonLogoWhiteText : amazonLogo}
              style={{
                width: 'auto',
                height: '1.875rem',
              }}
              alt={t(bundle, 'amazon_logo_alt')}
              loading="lazy"
              aria-hidden="true"
            />
          </Row>
        </AppLayoutHeader>
      )}
      <AppLayoutNav
        backgroundColor={'color.surface.bg-muted'}
        style={
          !hasHeader
            ? undefined
            : { position: 'sticky', top: '0px', zIndex: 100 }
        }
        maxWidth={AppLayoutMaxWidth.Full}
        role="banner"
      >
        <SecondaryNav
          style={{ backgroundColor: token('color.surface.bg-muted') }}
        >
          <SecondaryNavList
            backgroundColor={'color.surface.bg-muted'}
            style={{ justifyContent: 'flex-start' }}
          >
            {[
              { id: 'grow', ref: '#grow', text: 'grow_nav' },
              { id: 'thrive', ref: '#thrive', text: 'thrive_nav' },
              { id: 'connect', ref: '#connect', text: 'connect_nav' },
              { id: 'careers', ref: CAREERS_LINK, text: 'career_nav' },
            ].map((nav) => (
              <SecondaryNavLink
                key={nav.ref}
                href={nav.ref}
                {...(!nav.ref.startsWith('#') && {
                  target: '_blank',
                  rel: 'noopener noreferrer',
                  'aria-label': t(bundle, 'opens_in_new_tab', {
                    label: t(bundle, nav.text),
                  }),
                })}
                onClick={(e) => {
                  if (nav.ref.startsWith('#')) {
                    e.preventDefault();
                    navigateToSection(nav.ref);
                  }
                  trackClick({
                    elementId: nav.id,
                    elementType: 'nav',
                    pageGroup: NAV_LAYOUT_PAGE_GROUP,
                    metadata: { href: nav.ref },
                  });
                }}
              >
                {t(bundle, nav.text)}
              </SecondaryNavLink>
            ))}
          </SecondaryNavList>
        </SecondaryNav>
      </AppLayoutNav>
      <MainWithSkipLink>{children}</MainWithSkipLink>
      <Col width={'100%'} role="contentinfo">
        <Col
          {...{ [PREFERS_COLOR_SCHEME_ID]: ThemeMode.Dark }}
          backgroundColor={'color.surface.dark-bg-static'}
          width={'100%'}
          gridGap="dimensions.spacing.100"
          alignItems="center"
        >
          {(matches.m || matches.s) && (
            <Row
              dataTestId="logo-footer-test-id"
              padding={'dimensions.spacing.400'}
            >
              <img
                src={amazonLogoWhiteText}
                style={{
                  maxWidth: '5.625rem',
                  maxHeight: '1.875rem',
                  width: 'auto',
                  height: 'auto',
                }}
                alt={t(bundle, 'amazon_logo_alt')}
                loading="lazy"
                aria-hidden="true"
              />
            </Row>
          )}

          {footerWrapper([
            <Link
              key="footer-conditions-of-use"
              href={CONDITIONS_OF_USE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t(bundle, 'opens_in_new_tab', {
                label: t(bundle, 'footer_conditions_of_use'),
              })}
              textDecoration={LinkTextDecorationOptions.Underline}
              fontSize={'text.size.100'}
              onClick={() =>
                trackClick({
                  elementId: 'conditions-of-use',
                  elementType: 'link',
                  pageGroup: NAV_LAYOUT_PAGE_GROUP,
                  metadata: { href: CONDITIONS_OF_USE_LINK },
                })
              }
            >
              {t(bundle, 'footer_conditions_of_use')}
            </Link>,
            <Link
              key="footer-privacy-notice"
              href={PRIVACY_NOTICE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t(bundle, 'opens_in_new_tab', {
                label: t(bundle, 'footer_privacy_notice'),
              })}
              textDecoration={LinkTextDecorationOptions.Underline}
              fontSize={'text.size.100'}
              onClick={() =>
                trackClick({
                  elementId: 'privacy-notice',
                  elementType: 'link',
                  pageGroup: NAV_LAYOUT_PAGE_GROUP,
                  metadata: { href: PRIVACY_NOTICE_LINK },
                })
              }
            >
              {t(bundle, 'footer_privacy_notice')}
            </Link>,
            <Text
              key="footer-affiliates"
              color="color.text.primary"
              fontSize={'text.size.100'}
            >
              {t(bundle, 'footer_affiliates')}
            </Text>,
          ])}
        </Col>
      </Col>
    </AppLayout>
  );
};

export default GrowThriveConnectLayout;
